// components/public/QuickApplicationModal.tsx
/**
 * @description Quick application form modal for prospective tenants
 */
import { useState } from 'react';
import { X, CheckCircle, Loader2 } from 'lucide-react';

interface QuickApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  building: { id: string; name: string; address: string; email: string };
  unit?: { id?: string; type: string; price: number; floor: number };
}

type ApplicantType = 'professional' | 'student' | 'newcomer' | 'other';
type PetType = 'no' | 'dog' | 'cat' | 'other';

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  preferredMoveIn: string;
  applicantType: ApplicantType | '';
  hasPets: PetType;
}

interface FormErrors {
  fullName?: string;
  email?: string;
  phone?: string;
  applicantType?: string;
}

export function QuickApplicationModal({
  isOpen,
  onClose,
  building,
  unit,
}: QuickApplicationModalProps) {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    preferredMoveIn: '',
    applicantType: '',
    hasPets: 'no',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [applicationId, setApplicationId] = useState('');

  if (!isOpen) return null;

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData.applicantType) {
      newErrors.applicantType = 'Please select an option';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/applications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          buildingId: building.id,
          unitId: unit?.id,
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          preferredMoveIn: formData.preferredMoveIn || undefined,
          applicantType: formData.applicantType,
          hasPets: formData.hasPets,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit application');
      }

      setApplicationId(data.id);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Application submission error:', error);
      // Show error to user
      setErrors({ submit: 'Failed to submit application. Please try again.' } as FormErrors);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset form state when closing
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      preferredMoveIn: '',
      applicantType: '',
      hasPets: 'no',
    });
    setErrors({});
    setIsSubmitted(false);
    setApplicationId('');
    onClose();
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  // Success State
  if (isSubmitted) {
    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl animate-slide-up max-h-[90vh] overflow-hidden">
          <div className="p-6 text-center">
            {/* Success Icon */}
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle size={40} className="text-green-600" />
            </div>

            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Request Sent!
            </h2>

            <p className="text-gray-600 mb-4">
              Our leasing team will contact you within 24-48 hours.
            </p>

            <div className="bg-gray-50 rounded-lg p-3 mb-6">
              <p className="text-sm text-gray-500">Reference ID</p>
              <p className="font-mono font-semibold text-gray-900">
                {applicationId}
              </p>
            </div>

            <button
              onClick={handleClose}
              className="w-full py-4 bg-primary text-white font-medium rounded-xl hover:bg-primary-hover transition-colors"
            >
              Back to Building
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Form State
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      {/* Modal */}
      <div className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl animate-slide-up max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">
            Contact Leasing Team
          </h2>
          <button
            onClick={handleClose}
            className="p-2 -mr-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Unit Info */}
          <div className="bg-gray-50 rounded-lg p-3 mb-4">
            <p className="text-sm text-gray-500">Interested in</p>
            <p className="font-medium text-gray-900">{building.name}</p>
            {unit && (
              <p className="text-sm text-gray-600">
                {unit.type}, Floor {unit.floor} (${unit.price.toLocaleString()}
                /mo)
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
                className={`w-full h-12 px-4 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors ${errors.fullName ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                placeholder="Enter your full name"
              />
              {errors.fullName && (
                <p className="mt-1 text-sm text-red-600">{errors.fullName}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`w-full h-12 px-4 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors ${errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                placeholder="you@example.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={`w-full h-12 px-4 border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors ${errors.phone ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                placeholder="(416) 555-0123"
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
              )}
            </div>

            {/* Preferred Move-in */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preferred Move-in Date
              </label>
              <input
                type="date"
                value={formData.preferredMoveIn}
                onChange={(e) =>
                  handleInputChange('preferredMoveIn', e.target.value)
                }
                className="w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
              />
            </div>

            {/* Applicant Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                I am a... <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {[
                  { value: 'professional', label: 'Working professional' },
                  { value: 'student', label: 'Student' },
                  { value: 'newcomer', label: 'Newcomer to Canada' },
                  { value: 'other', label: 'Other' },
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${formData.applicantType === option.value
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:border-gray-300'
                      }`}
                  >
                    <input
                      type="radio"
                      name="applicantType"
                      value={option.value}
                      checked={formData.applicantType === option.value}
                      onChange={(e) =>
                        handleInputChange('applicantType', e.target.value)
                      }
                      className="w-4 h-4 text-primary focus:ring-primary"
                    />
                    <span className="text-gray-900">{option.label}</span>
                  </label>
                ))}
              </div>
              {errors.applicantType && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.applicantType}
                </p>
              )}
            </div>

            {/* Pets */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Do you have pets?
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'no', label: 'No' },
                  { value: 'dog', label: 'Yes - Dog' },
                  { value: 'cat', label: 'Yes - Cat' },
                  { value: 'other', label: 'Yes - Other' },
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-center justify-center gap-2 p-3 border rounded-lg cursor-pointer transition-colors text-sm ${formData.hasPets === option.value
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 hover:border-gray-300'
                      }`}
                  >
                    <input
                      type="radio"
                      name="hasPets"
                      value={option.value}
                      checked={formData.hasPets === option.value}
                      onChange={(e) =>
                        handleInputChange('hasPets', e.target.value)
                      }
                      className="sr-only"
                    />
                    <span className="text-gray-900">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </form>
        </div>

        {/* Sticky Submit Button */}
        <div className="p-4 border-t border-gray-100 flex-shrink-0 bg-white">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full py-4 bg-primary text-white font-medium rounded-xl hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Submitting...
              </>
            ) : (
              'Send Request'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
